/**
 * GatewayLink: the BLE requests of SahasForge ADR 0002, as the web console sends them. The same contract as the
 * Android app's `GatewayLink` (features/gateway/GatewaySeams.kt), whose `LiveGatewayBle` carries them over Bluetooth;
 * here `remoteGatewayLink` carries them over the cloud command channel (POST /v1/admin/gateways/{id}/ops, ADR 0002
 * Amendment A22). Same opcodes, same bodies, same answers: the gateway answers both with one dispatcher.
 *
 * P0 (docs/plans/2026-10-18_remote-gateway-console.md) has the read methods only. A request answers its body, or
 * throws a GatewayOpError: the gateway's ERROR answer (opcode 0x80FF, {error, detail, opcode}), or the cloud's refusal
 * (offline, not a remote read, rate limited, no answer in time). P2 moves the opcodes and bodies into forge-protocol
 * and checks these types against it.
 */

import { adminGatewaysApi, type AdminGatewayOpResult } from "@/lib/api/admin-gateways";
import { errorCodeOf, errorDetailOf, statusOf } from "@/lib/api/error";

/** The request opcodes the console sends (ADR 0002), with their answers. */
export const OP = {
  INFO_GET: 0x0010,
  IDENTITY_GET: 0x0012,
  STATUS_GET: 0x00f0,
  NET_GET: 0x0022,
  WIFI_LIST: 0x0024,
  PING: 0x0062,
  HEALTH_CHECK: 0x0063,
  DEVICES_GET: 0x0090,
  POINT_READ: 0x0091,
  LOG_TAIL: 0x0092,
  BT_RADIO_GET: 0x0080,
  CRANE_GET: 0x0070,
  CRANE_LIVE: 0x0074,
  CRANE_UPLINK_GET: 0x0075,
  CRANE_ACCEL_GET: 0x0078,
} as const;

export type OpName = keyof typeof OP;

/** The ERROR answer's opcode. */
export const OP_ERROR = 0x80ff;

/** An opcode's ADR 0002 name, else its hex. */
export function opLabel(opcode: number): string {
  const name = (Object.keys(OP) as OpName[]).find((k) => OP[k] === opcode);
  return name ?? `0x${opcode.toString(16).padStart(4, "0").toUpperCase()}`;
}

// ── Answers (GatewayPro internal/provision; ADR 0002) ───────────────────────

/** INFO (Amendment A1). */
export interface GatewayInfoDoc {
  proto: number;
  serial: string;
  hw_rev: string;
  fw: string;
  fw_line?: string;
  model: string;
  name: string;
  prov: boolean;
  device: string;
  caps: string[];
  thing_id?: string;
  cert_cn?: string;
}

/** IDENTITY (Amendment A10.2). */
export interface GatewayIdentityDoc {
  name: string;
  serial: string;
  identity_source: string;
  factory_name?: string;
  model: string;
  board_serial?: string;
  macs: { ble?: string; eth?: string; wifi?: string };
  fw: string;
  fw_line?: string;
  hw_rev: string;
  thing_id?: string;
  cert_cn?: string;
}

/** STATUS (contract §6.2, A2, A9, A11). */
export interface GatewayStatusDoc {
  state: string;
  error: string;
  detail: string;
  ip: string;
  rssi: number;
  ts: number;
  link: string;
  iface: string;
  net_pending?: { iface?: string; remaining_s?: number; by?: string } | null;
  stage: string;
  label: string;
  attempt?: number;
  linked_once: boolean;
}

/** One interface in NET (A3, A9, A17.1, A21, A27.6). */
export interface GatewayNetIface {
  name: string;
  kind: string;
  /** The current MAC; NetworkManager can randomise it while a Wi-Fi adapter scans. */
  mac: string;
  /** A27.6: the permanent (burned-in) MAC, upper case, "" when unknown. A plant's allow-list needs this one. */
  perm_mac?: string;
  /** A27.6: the network and signal (dBm) of an associated Wi-Fi interface. */
  ssid?: string;
  rssi?: number;
  up: boolean;
  carrier: boolean;
  method: string;
  ipv4: string[];
  gateway: string;
  dns: string[];
  default_route: boolean;
  never_default: boolean;
  bus?: string;
  pending: boolean;
  enabled?: boolean;
  dns_auto?: boolean;
  /** A21: the saved address of a port without link. */
  saved_ipv4?: string[];
}

/**
 * NET_GET's answer (A3, A27.6). `control` is NetworkManager's network-control permission for the gateway's user:
 * "yes", "auth" (it would need a password: the polkit rule is missing), "no", or "" when unknown. Older gateways omit it.
 */
export interface GatewayNetDoc {
  ifaces: GatewayNetIface[];
  control?: "yes" | "auth" | "no" | "";
}

/** One saved Wi-Fi network in WIFI_LIST (A9); never a secret. */
export interface GatewaySavedWifi {
  ssid: string;
  active: boolean;
  hidden: boolean;
  secured: boolean;
  autoconnect: boolean;
  priority: number;
  iface?: string;
}

/** PING's request (A12): an interface and its router, DNS server, the broker, or an IPv4 address. */
export interface PingRequest {
  iface: string;
  target: string;
  count?: number;
  port?: number;
}

/** PING_RES (A12). */
export interface PingResult {
  ok: boolean;
  iface: string;
  target: string;
  ip: string;
  method: string;
  sent: number;
  received: number;
  min_ms: number | null;
  avg_ms: number | null;
  max_ms: number | null;
  detail?: string;
}

/** One row of HEALTH (A12). */
export interface HealthCheckRow {
  id: string;
  group: string;
  label: string;
  status: string;
  value?: number | string;
  unit?: string;
  detail?: string;
}

/** HEALTH (A12). */
export interface HealthReport {
  ok: boolean;
  overall: string;
  ts: number;
  duration_ms: number;
  checks: HealthCheckRow[];
}

/** One PLC device in DEVICES (A17.5). */
export interface PlcDeviceDiag {
  name: string;
  driver: string;
  family: string;
  endpoint: string;
  state: string;
  poll_s: number;
  points: number;
  points_bad: number;
  last_ok_ms_ago: number | null;
  last_error: string;
  last_error_ms_ago: number | null;
  reads: number;
  read_errors: number;
  reconnects: number;
  latency_ms: { last: number; avg: number; max: number };
}

/** DEVICES (A17.5). */
export interface DevicesReport {
  devices: PlcDeviceDiag[];
  truncated?: number;
}

/** POINT_READ's request (A17.5): a configured device's metric. */
export interface PointReadRequest {
  device: string;
  metric: string;
}

/** POINT_VALUE (A17.5). */
export interface PointReadResult {
  ok: boolean;
  value: unknown;
  raw?: unknown;
  quality: string;
  latency_ms: number;
  error?: string;
}

/** LOG_TAIL's request (A17.5). */
export interface LogRequest {
  lines?: number;
  level?: string;
  grep?: string;
}

/** One line of LOG_LINES (A17.5): t in Unix milliseconds. */
export interface LogLine {
  t: number;
  level: string;
  msg: string;
  attrs?: Record<string, unknown>;
}

/** LOG_LINES (A17.5). */
export interface LogTail {
  lines: LogLine[];
  dropped: number;
}

/** BT_RADIO (A15). */
export interface BtRadioDoc {
  radios: { id: string; address: string; bus: string; label: string; powered: boolean; link: boolean; default: boolean }[];
  link: string;
  pending: { from: string; to: string; remaining_s: number } | null;
}

/** The read methods of ADR 0002, as the app's GatewayLink names them. */
export interface GatewayLink {
  info(): Promise<GatewayInfoDoc>;
  identity(): Promise<GatewayIdentityDoc>;
  fetchStatus(): Promise<GatewayStatusDoc>;
  netGet(): Promise<GatewayNetDoc>;
  wifiList(): Promise<GatewaySavedWifi[]>;
  ping(request: PingRequest): Promise<PingResult>;
  healthCheck(only?: string[]): Promise<HealthReport>;
  devicesGet(): Promise<DevicesReport>;
  pointRead(request: PointReadRequest): Promise<PointReadResult>;
  logTail(request?: LogRequest): Promise<LogTail>;
  btRadioGet(): Promise<BtRadioDoc>;
  craneGet(): Promise<Record<string, unknown>>;
  craneLive(): Promise<Record<string, unknown>>;
  craneUplinkGet(): Promise<Record<string, unknown>>;
  craneAccelGet(): Promise<Record<string, unknown>>;
}

// ── Errors ──────────────────────────────────────────────────────────────────

/**
 * Why a request has no answer:
 * - gateway_error: the gateway answered ERROR (code and detail are its own, as the app shows them);
 * - offline, not_claimed: the cloud did not send it (never queued);
 * - refused: not a remote read, or the gateway's remote_ops does not list it;
 * - rejected: the gateway refused it (expired, not a remote opcode, busy);
 * - timeout: no answer in time; rate_limited: too many ops; failed: anything else.
 */
export type GatewayOpErrorKind =
  | "gateway_error"
  | "offline"
  | "not_claimed"
  | "refused"
  | "rejected"
  | "timeout"
  | "rate_limited"
  | "failed";

export class GatewayOpError extends Error {
  readonly kind: GatewayOpErrorKind;
  /** The gateway's ERROR code (E_BUSY…) or the cloud's error code (gateway_offline…). */
  readonly code: string;
  /** The reason, in words. */
  readonly detail: string;
  /** The request's opcode. */
  readonly opcode: number;
  /** Seconds to wait before trying again (429). */
  readonly retryAfterS: number | null;

  constructor(kind: GatewayOpErrorKind, code: string, detail: string, opcode: number, retryAfterS: number | null = null) {
    super(`${opLabel(opcode)}: ${detail || code}`);
    this.name = "GatewayOpError";
    this.kind = kind;
    this.code = code;
    this.detail = detail;
    this.opcode = opcode;
    this.retryAfterS = retryAfterS;
  }
}

/** The cloud's refusal of an op, as a GatewayOpError. */
export function opErrorOf(err: unknown, opcode: number): GatewayOpError {
  if (err instanceof GatewayOpError) return err;
  const status = statusOf(err);
  const code = errorCodeOf(err) ?? (status ? `http_${status}` : "network");
  const detail = errorDetailOf(err) ?? "";
  const retry =
    typeof err === "object" && err !== null && "retryAfterS" in err
      ? ((err as { retryAfterS?: number | null }).retryAfterS ?? null)
      : null;
  const kind: GatewayOpErrorKind = (() => {
    switch (code) {
      case "gateway_offline":
        return "offline";
      case "gateway_not_claimed":
        return "not_claimed";
      case "opcode_not_allowed":
      case "opcode_not_offered":
        return "refused";
      case "op_rejected":
        return "rejected";
      case "gateway_timeout":
        return "timeout";
      case "rate_limited":
        return "rate_limited";
    }
    return status === 504 ? "timeout" : status === 429 ? "rate_limited" : "failed";
  })();
  return new GatewayOpError(kind, code, detail, opcode, retry);
}

/** An answer's body, or the gateway's ERROR answer as a GatewayOpError. */
export function answerBody<T>(res: Pick<AdminGatewayOpResult, "answer">, opcode: number): T {
  if (res.answer.opcode === OP_ERROR) {
    const e = (res.answer.body ?? {}) as { error?: unknown; detail?: unknown };
    throw new GatewayOpError(
      "gateway_error",
      typeof e.error === "string" ? e.error : "E_UNKNOWN",
      typeof e.detail === "string" ? e.detail : "",
      opcode,
    );
  }
  return res.answer.body as T;
}

/** Sends one request: the function that carries it, so a test can stand in for the API. */
export type OpSender = (opcode: number, body: Record<string, unknown>) => Promise<Pick<AdminGatewayOpResult, "answer">>;

/** A GatewayLink over a sender. */
export function gatewayLinkOver(send: OpSender): GatewayLink {
  async function request<T>(opcode: number, body: Record<string, unknown> = {}): Promise<T> {
    let res: Pick<AdminGatewayOpResult, "answer">;
    try {
      res = await send(opcode, body);
    } catch (err) {
      throw opErrorOf(err, opcode);
    }
    return answerBody<T>(res, opcode);
  }
  const obj = (body: object): Record<string, unknown> => ({ ...(body as Record<string, unknown>) });
  return {
    info: () => request(OP.INFO_GET),
    identity: () => request(OP.IDENTITY_GET),
    fetchStatus: () => request(OP.STATUS_GET),
    netGet: () =>
      request<{ ifaces?: GatewayNetIface[]; control?: GatewayNetDoc["control"] }>(OP.NET_GET).then((r) => ({
        ...r,
        ifaces: r.ifaces ?? [],
      })),
    wifiList: () => request<{ networks?: GatewaySavedWifi[] }>(OP.WIFI_LIST).then((r) => r.networks ?? []),
    ping: (req) => request(OP.PING, obj(req)),
    healthCheck: (only) => request(OP.HEALTH_CHECK, only && only.length > 0 ? { only } : {}),
    devicesGet: () => request(OP.DEVICES_GET),
    pointRead: (req) => request(OP.POINT_READ, obj(req)),
    logTail: (req) => request(OP.LOG_TAIL, obj(req ?? {})),
    btRadioGet: () => request(OP.BT_RADIO_GET),
    craneGet: () => request(OP.CRANE_GET),
    craneLive: () => request(OP.CRANE_LIVE),
    craneUplinkGet: () => request(OP.CRANE_UPLINK_GET),
    craneAccelGet: () => request(OP.CRANE_ACCEL_GET),
  };
}

/** The GatewayLink of one gateway, over the cloud command channel (POST /v1/admin/gateways/{id}/ops). */
export function remoteGatewayLink(gatewayId: string): GatewayLink {
  return gatewayLinkOver((opcode, body) => adminGatewaysApi.op(gatewayId, opcode, body));
}

/** Words for a request that has no answer, for the console. */
export function opErrorWords(e: GatewayOpError): string {
  switch (e.kind) {
    case "gateway_error":
      return `The gateway answered ${e.code}${e.detail ? `: ${e.detail}` : ""}.`;
    case "offline":
      return "The gateway is offline. An op is never queued: try again when it is online.";
    case "not_claimed":
      return "The gateway has no certificate yet, so it cannot take commands.";
    case "refused":
      return e.code === "opcode_not_offered"
        ? "This gateway does not take this request remotely (older firmware, or it lacks that part)."
        : "This request is not a remote read. P0 takes reads only.";
    case "rejected":
      return `The gateway refused the request${e.detail ? `: ${e.detail}` : ""}.`;
    case "timeout":
      return "The gateway did not answer in time. It may have gone offline.";
    case "rate_limited":
      return `Too many remote requests. Try again${e.retryAfterS ? ` in ${e.retryAfterS} s` : " soon"}.`;
  }
  return `The request failed${e.detail ? `: ${e.detail}` : ` (${e.code})`}.`;
}
